import api from './api'

export const exportsService = {
  /**
   * Create a new export job
   * @param {Object} exportRequest - Export configuration
   * @returns {Promise} Export response with job details
   */
  async createExport(exportRequest) {
    const response = await api.post('/exports/', exportRequest)
    return response.data
  },

  /**
   * Get export job status
   * @param {string} exportId - Export job ID
   * @returns {Promise} Export job details
   */
  async getExportStatus(exportId) {
    const response = await api.get(`/exports/${exportId}`)
    return response.data
  },

  /**
   * Download export file
   * @param {string} exportId - Export job ID
   * @returns {Promise} File blob
   */
  async downloadExport(exportId) {
    const response = await api.get(`/exports/${exportId}/download`, {
      responseType: 'blob'
    })
    return response.data
  },

  /**
   * Get user's export history
   * @param {Object} params - Query parameters
   * @returns {Promise} List of exports
   */
  async getExportHistory(params = {}) {
    const response = await api.get('/exports/', { params })
    return response.data
  },

  /**
   * Cancel an export job
   * @param {string} exportId - Export job ID
   * @returns {Promise} Cancellation response
   */
  async cancelExport(exportId) {
    const response = await api.delete(`/exports/${exportId}`)
    return response.data
  },

  /**
   * Export data with advanced options
   * @param {number} dataInputId - Data input ID
   * @param {Object} options - Export options
   * @returns {Promise} Export result or download
   */
  async exportData(dataInputId, options = {}) {
    const {
      format = 'csv',
      includeHeaders = true,
      includeMetadata = false,
      includeDeleted = false,
      selectedColumns = null,
      filename = null,
      filters = [],
      searchQuery = null,
      dateRangeEnabled = false,
      dateFrom = null,
      dateTo = null
    } = options

    // Prepare filters array with date range if enabled
    const allFilters = [...filters]

    // Add date range filters if enabled
    if (dateRangeEnabled) {
      if (dateFrom) {
        allFilters.push({
          column: 'created_at',
          operator: 'gte',
          value: dateFrom
        })
      }
      if (dateTo) {
        // Add one day to include the entire end date
        const endDate = new Date(dateTo)
        endDate.setDate(endDate.getDate() + 1)
        allFilters.push({
          column: 'created_at',
          operator: 'lt',
          value: endDate.toISOString().split('T')[0]
        })
      }
    }

    // Prepare filter request
    const filterRequest = {
      data_input_id: dataInputId,
      filters: allFilters,
      search_query: searchQuery,
      skip: 0,
      limit: 1000, // Maximum allowed limit - backend will handle pagination for exports
      include_deleted: includeDeleted, // Use the actual includeDeleted parameter
      include_total_count: false
    }

    // Prepare export request
    const exportRequest = {
      filter_request: filterRequest,
      export_format: format,
      include_headers: includeHeaders,
      include_metadata: includeMetadata,
      include_deleted: includeDeleted,
      selected_columns: selectedColumns && selectedColumns.length > 0 ? selectedColumns : null,
      filename: filename
    }

    // Debug logging to verify parameters
    console.log('🐛 Frontend Export Request:', {
      includeDeleted,
      includeMetadata,
      filterRequest: {
        include_deleted: filterRequest.include_deleted
      },
      exportRequest: {
        include_deleted: exportRequest.include_deleted,
        include_metadata: exportRequest.include_metadata
      }
    })

    try {
      // First try without responseType to see if we get JSON (export job) or blob (direct download)
      const response = await api.post(`/data-inputs/${dataInputId}/records/export`, exportRequest)

      // Check if response is JSON (export job) or blob (direct download)
      const contentType = response.headers['content-type'] || ''

      if (contentType.includes('application/json')) {
        // Response is JSON - it's an export job, we need to poll for completion
        const exportJob = response.data

        if (exportJob.status === 'completed' && exportJob.download_url) {
          // Job completed immediately, download the file
          const fileBlob = await this.downloadExport(exportJob.export_id)
          return {
            success: true,
            blob: fileBlob,
            filename: exportJob.filename || `export_${exportJob.export_id}.${format}`,
            recordCount: exportJob.record_count
          }
        } else {
          // Poll for completion
          return await this.pollExportCompletion(exportJob.export_id, format)
        }
      } else {
        // Response is a blob - direct download
        const blob = new Blob([response.data], {
          type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })

        // Get filename from response headers or use default
        const contentDisposition = response.headers['content-disposition']
        let downloadFilename = filename || `export_${Date.now()}.${format}`

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
          if (filenameMatch && filenameMatch[1]) {
            downloadFilename = filenameMatch[1].replace(/['"]/g, '')
          }
        }

        return {
          success: true,
          blob: blob,
          filename: downloadFilename,
          recordCount: null
        }
      }

    } catch (error) {
      console.error('Export failed:', error)
      throw new Error(error.response?.data?.detail || 'Export failed')
    }
  },

  /**
   * Poll export job until completion
   * @param {string} exportId - Export job ID
   * @param {string} format - Export format
   * @returns {Promise} Export result
   */
  async pollExportCompletion(exportId, format) {
    const maxAttempts = 30 // 30 attempts with 2-second intervals = 1 minute max
    let attempts = 0

    while (attempts < maxAttempts) {
      try {
        const status = await this.getExportStatus(exportId)
        
        if (status.status === 'completed') {
          const fileBlob = await this.downloadExport(exportId)
          return {
            success: true,
            blob: fileBlob,
            filename: status.filename || `export_${exportId}.${format}`,
            recordCount: status.record_count
          }
        }
        
        if (status.status === 'failed') {
          throw new Error(status.error_message || 'Export failed')
        }
        
        // Wait 2 seconds before next poll
        await new Promise(resolve => setTimeout(resolve, 2000))
        attempts++
        
      } catch (error) {
        if (attempts === maxAttempts - 1) {
          throw error
        }
        await new Promise(resolve => setTimeout(resolve, 2000))
        attempts++
      }
    }
    
    throw new Error('Export timeout - please try again')
  },

  /**
   * Download blob as file
   * @param {Blob} blob - File blob
   * @param {string} filename - Filename
   */
  downloadBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }
}
